/* eslint-disable react/no-unstable-nested-components */
import { Box, Button, Separator, HStack, Text } from '@chakra-ui/react';
import { PageContext } from 'components/common/PageContext';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import {
  useDuplicateWorksheet,
  useGetWorksheet,
  useSaveWorksheet,
  WorksheetSaveIntent,
  Worksheet as WorksheetType,
} from 'hooks/api/backend/worksheets/useWorksheets';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DefaultReactSuggestionItem, getDefaultReactSlashMenuItems } from '@blocknote/react';
import '@blocknote/mantine/style.css';
import {
  Block,
  BlockNoteEditor,
  BlockNoteSchema,
  BlockSchema,
  BlockSchemaFromSpecs,
  BlockSpecs,
  defaultBlockSpecs,
} from '@blocknote/core';
import {
  createTextQuestionSlashItem,
  TextQuestionBlock,
} from 'components/worksheet/Blocks/TextQuestion/TextQuestionBlock';
import { WorksheetPreview } from 'components/worksheet/WorksheetPreview';
import 'styles/worksheets/styles.css';
import { createEditorWithMode } from 'utils/createEditorWithMode';
import {
  createMultipleChoiceSlashItem,
  MultipleChoiceBlock,
} from 'components/worksheet/Blocks/MultipleChoice/MultipleChoiceBlock';
import {
  createSelfAssessmentSlashItem,
  SelfAssessmentBlock,
} from 'components/worksheet/Blocks/SelfAssessment/SelfAssessmentBlock';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { de } from 'assets/dictionary/de';
import {
  createNHVideoBlockSlashItem,
  NHVideoBlock,
} from 'components/worksheet/Blocks/NHVideo/NHVideoBlock';
import {
  DiagramIcon,
  EditIcon,
  VisibilityIcon,
  DocumentIcon,
  SaveIcon,
  PrintIcon,
} from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import useAutoSave, { SaveStatus } from 'hooks/useAutoSave';
import { ProtectedRoutes } from 'types/Routes';
import { SaveIndicator } from 'components/worksheet/SaveIndicator';
import { WorksheetReviewContent } from 'components/worksheet/Review/WorksheetReviewContent';
import { useBreadcrumbs } from 'hooks/utils/useBreadcrumbs';
import { WorksheetHeader } from 'components/worksheet/WorksheetHeader';
import { WorksheetEditor } from 'components/worksheet/WorksheetEditor';
import { WorksheetDescription } from 'components/worksheet/WorksheetDescription';
import { Breadcrumb } from 'types/breadcrumbs';
import { AxiosError, HttpStatusCode } from 'axios';
import { getWorksheetDashboardBreadcrumbPath } from 'utils/worksheetDashboardReturnParams';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { useUser } from 'utils/auth';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { printWorksheet } from 'utils/worksheetPrinting';
import { useFeatureFlagsContext } from 'hooks/utils/useFeatureFlagContext';

const noop = (): void => undefined;

type ModalSaveMode = 'DISTRIBUTED' | 'DISTRIBUTED_TEMPLATE' | 'TEMPLATE_ONLY';
type WorksheetConflictResponse = {
  message?: string;
  reason?: string;
  requestedIntent?: WorksheetSaveIntent;
  worksheetState?: string;
  isDistributed?: boolean;
  isTemplateLinked?: boolean;
};

// List containing all default Slash Menu Items, as well as our custom one.
const getCustomSlashMenuItems = <T extends BlockSchema>(
  editor: BlockNoteEditor<T>
): DefaultReactSuggestionItem[] => {
  const itemsToFilterOut = new Set(['Emoji']);

  const items = [
    ...getDefaultReactSlashMenuItems(editor),
    createMultipleChoiceSlashItem(editor),
    createNHVideoBlockSlashItem(editor),
    createTextQuestionSlashItem(editor),
    createSelfAssessmentSlashItem(editor),
    // createNHArticleBlockSlashItem(editor),
  ];

  return Object.values(
    items
      .filter((item) => !itemsToFilterOut.has(item.title)) // Filter out unwanted items
      // Group items by their group property (manually done, to enable sorting per group)
      .reduce<Record<string, DefaultReactSuggestionItem[]>>((acc, item) => {
        const groupKey = item.group ?? 'Sonstige';
        acc[groupKey] ??= [];
        acc[groupKey].push(item);
        return acc;
      }, {})
  ).flatMap((groupItems) => groupItems.toSorted((a, b) => a.title.localeCompare(b.title, 'de'))); // Sort the groups by their keys
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { audio, image, checkListItem, codeBlock, file, table, video, ...remainingBlockSpecs } =
  defaultBlockSpecs;

export const schema = BlockNoteSchema.create({
  blockSpecs: {
    ...remainingBlockSpecs,
    textQuestion: TextQuestionBlock,
    multipleChoice: MultipleChoiceBlock,
    selfAssessment: SelfAssessmentBlock,
    nhVideo: NHVideoBlock,
    // nhArticle: NHArticleBlock,
  },
});
export type WorksheetBlockSchema = typeof schema.blockSchema;

/**
 * CustomBlockContentSpec is a type that represents the block specifications including the custom
 * block types defined in the schema.
 * It is a union of the default block specifications and any custom block specifications.
 */
export type CustomBlockContentSpec = typeof schema.blockSpecs;

type WorksheetMode = 'edit' | 'preview' | 'review';

export const Worksheet = (): ReactElement => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { data: user } = useUser();

  const {
    data: worksheetData,
    isLoading,
    isError,
    error,
    refetch: refetchWorksheet,
  } = useGetWorksheet(id);
  const toast = useNovaToast();
  const modalService = useModalService();
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const { addBreadcrumbs, removeBreadcrumbs } = useBreadcrumbs();
  const featureFlags = useFeatureFlagsContext();

  const [initialContent, setInitialContent] = useState<CustomBlockContentSpec[] | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [worksheetTitle, setWorksheetTitle] = useState<string>(
    worksheetData?.title || 'Arbeitsblatt'
  );
  const [worksheetDescription, setWorksheetDescription] = useState<string>(
    worksheetData?.description || ''
  );

  const [isTitleEditable, setIsTitleEditable] = useState(false);
  const [isDescriptionEditable, setIsDescriptionEditable] = useState(false);
  const [shouldShowEditor, setShouldShowEditor] = useState<boolean>(
    !!initialContent && initialContent?.length !== 0
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(SaveStatus.IDLE);
  const modeParam = searchParams.get('mode');
  const breadcrumbClassIdParam = searchParams.get('classId');
  const breadcrumbClassNameParam = searchParams.get('className');

  const resolvedMode = useMemo<WorksheetMode>(() => {
    if (modeParam === 'edit' || modeParam === 'preview' || modeParam === 'review') {
      return modeParam;
    }
    return 'edit';
  }, [modeParam]);

  const isOwner = Boolean(
    worksheetData?.createdById && user?.id && worksheetData.createdById === user.id
  );

  const effectiveMode = useMemo<WorksheetMode>(() => {
    // Override edit -> preview if user is not owner
    if (worksheetData && user && !isOwner && resolvedMode === 'edit') return 'preview';
    return resolvedMode;
  }, [isOwner, resolvedMode, worksheetData, user]);

  const isPreviewMode = effectiveMode === 'preview';
  const isReviewMode = effectiveMode === 'review';
  const isEditMode = effectiveMode === 'edit';
  const conflictHandledRef = React.useRef(false);
  const isTemplateLinked = Boolean(
    worksheetData?.isSharedAsTemplate && worksheetData?.sharedTemplateId
  );

  const showWorksheetConflictToast = useCallback(
    (error: AxiosError): void => {
      const status = error.response?.status;
      if (status !== HttpStatusCode.Conflict) {
        toast.show({ variant: ToastVariant.ERROR });
        return;
      }

      const conflictData = error.response?.data as WorksheetConflictResponse | undefined;
      const reason = conflictData?.reason ?? '';
      const requestedIntent = conflictData?.requestedIntent;
      const isDistributedNow = Boolean(conflictData?.isDistributed);

      const attemptedSimpleSave =
        requestedIntent === WorksheetSaveIntent.AUTO_SAVE ||
        requestedIntent === WorksheetSaveIntent.MANUAL_SAVE;
      const attemptedTemplateUpdate =
        requestedIntent === WorksheetSaveIntent.MANUAL_SAVE_PROPAGATE_TEMPLATE;

      if ((attemptedSimpleSave || attemptedTemplateUpdate) && isDistributedNow) {
        toast.show({
          variant: ToastVariant.WARNING,
          title: 'Arbeitsblatt wurde inzwischen beantwortet',
          description:
            'Während deiner Bearbeitung sind neue Antworten eingegangen. Bitte klicke erneut auf speichern und wähle aus, ob du eine Kopie speichern oder mit Anpassungen fortfahren möchtest.',
        });
        return;
      }

      if (reason.includes('not allowed for the worksheet state')) {
        toast.show({
          variant: ToastVariant.WARNING,
          title: 'Speichern gerade nicht möglich',
          description:
            'Der Status des Arbeitsblatts hat sich geändert. Bitte versuche es noch einmal.',
        });
        return;
      }

      if (reason.includes('requires a template link')) {
        toast.show({
          variant: ToastVariant.WARNING,
          title: 'Vorlage nicht mehr verknüpft',
          description:
            'Dieses Arbeitsblatt ist aktuell nicht mehr mit einer Vorlage verknüpft. Bitte aktualisiere die Seite.',
        });
        return;
      }

      if (reason.includes('requires distributed answers')) {
        toast.show({
          variant: ToastVariant.WARNING,
          title: 'Keine Antworten zum Löschen vorhanden',
          description:
            'Für diese Aktion müssen Antworten vorhanden sein. Aktuell gibt es keine Antworten zum Löschen.',
        });
        return;
      }

      if (reason.includes('no linked template rows were updated')) {
        toast.show({
          variant: ToastVariant.WARNING,
          title: 'Vorlagenaktualisierung nicht möglich',
          description:
            'Die verknüpfte Vorlage konnte nicht aktualisiert werden. Bitte aktualisiere die Seite und versuche es erneut.',
        });
        return;
      }

      toast.show({ variant: ToastVariant.ERROR });
    },
    [toast]
  );

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('mode', resolvedMode);
    if (modeParam !== resolvedMode) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [modeParam, resolvedMode, searchParams, setSearchParams]);

  // Sync URL to preview when non-owner opens with mode=edit
  useEffect(() => {
    if (!user || !worksheetData) {
      return;
    }
    if (!isOwner && modeParam === 'edit') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('mode', 'preview');
      setSearchParams(nextParams, { replace: true });
    }
  }, [isOwner, modeParam, searchParams, setSearchParams, user, worksheetData]);

  useEffect(() => {
    if (!id) return undefined;
    const breadcrumbs: Breadcrumb[] = [
      {
        path: getWorksheetDashboardBreadcrumbPath(),
        name: 'Arbeitsblatt-Verwaltung',
      },
    ];

    if (breadcrumbClassIdParam && breadcrumbClassNameParam) {
      breadcrumbs.push({
        path: `${ProtectedRoutes.WORKSHEET_CLASS_DETAIL}/${breadcrumbClassIdParam}`,
        name: breadcrumbClassNameParam,
      });
    }

    breadcrumbs.push({
      path: `${ProtectedRoutes.WORKSHEET_DETAIL}/${id}`,
      name: worksheetData?.title || 'Arbeitsblatt',
    });

    addBreadcrumbs(breadcrumbs);

    return () => {
      removeBreadcrumbs();
    };
  }, [
    addBreadcrumbs,
    breadcrumbClassIdParam,
    breadcrumbClassNameParam,
    id,
    removeBreadcrumbs,
    worksheetData?.title,
  ]);

  const titleInputRef = React.useRef<HTMLInputElement>(null);
  const descriptionInputRef = React.useRef<HTMLTextAreaElement>(null);

  const editor = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const createEditor = (content: BlockSpecs[] | null) => {
      return createEditorWithMode('author', {
        schema,
        ...(content && content.length !== 0 && { initialContent: content }),
        dictionary: de,
        sideMenuDetection: 'viewport',
      });
    };

    return createEditor(initialContent);
  }, [initialContent]);

  const { mutateAsync: saveWorksheet } = useSaveWorksheet();

  const showEditor = (): void => {
    setShouldShowEditor(true);
    setTimeout(() => {
      editor.focus();
      editor.openSuggestionMenu('/');
    }, 10);
  };

  // Load initial content from the worksheet data or local storage to editor
  useEffect(() => {
    async function loadFromStorage(): Promise<BlockSpecs[] | undefined> {
      const storageString = localStorage.getItem(`editorContent-${id}`);
      return storageString ? (JSON.parse(storageString) as CustomBlockContentSpec[]) : undefined;
    }

    const loadContent = async (): Promise<void> => {
      if (!worksheetData) return;

      const worksheetContent = worksheetData.content;
      if (worksheetContent && worksheetContent.length > 0) {
        setInitialContent(worksheetContent);
        setShouldShowEditor(true);
        return;
      }

      const localContent = await loadFromStorage();
      if (localContent && localContent.length > 0) {
        setInitialContent(localContent);
        setShouldShowEditor(true);
        return;
      }

      setInitialContent([]);
      setShouldShowEditor(false);
    };

    loadContent();
    setWorksheetTitle(worksheetData?.title || 'Arbeitsblatt');
    setWorksheetDescription(worksheetData?.description || '');
  }, [worksheetData, id]);

  async function saveToStorage(
    jsonBlocks: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]
  ): Promise<void> {
    localStorage.setItem(`editorContent-${id}`, JSON.stringify(jsonBlocks));
  }

  const removeFromStorage = useCallback(async (): Promise<void> => {
    localStorage.removeItem(`editorContent-${id}`);
  }, [id]);

  const { mutateAsync: duplicateWorksheet } = useDuplicateWorksheet();
  const navigate = useNavigate();

  const handleDistributedConflict = useCallback(
    async (
      latestWorksheet: WorksheetType | undefined,
      document: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]
    ): Promise<void> => {
      if (!latestWorksheet?.isDistributed || !id) return;
      const latestIsTemplateLinked = Boolean(
        latestWorksheet.isSharedAsTemplate && latestWorksheet.sharedTemplateId
      );

      modalService.show(ModalVariant.WORKSHEET_DISTRIBUTION_SAVE, {
        mode: latestIsTemplateLinked ? 'DISTRIBUTED_TEMPLATE' : 'DISTRIBUTED',
        onCopyAndSave: async () => {
          const copiedWorksheet = await duplicateWorksheet({
            id,
            modifiedContent: document,
          });

          navigate(`${ProtectedRoutes.WORKSHEET_DASHBOARD}/${copiedWorksheet.id}`);
          await removeFromStorage();
          setHasUnsavedChanges(false);
        },
        onPrimarySave: async () => {
          try {
            await saveWorksheet({
              id,
              title: worksheetTitle,
              description: worksheetDescription,
              content: document,
              intent: latestIsTemplateLinked
                ? WorksheetSaveIntent.MANUAL_SAVE_DELETE_ANSWERS_PROPAGATE_TEMPLATE
                : WorksheetSaveIntent.MANUAL_SAVE_DELETE_ANSWERS,
            });

            await removeFromStorage();
            setHasUnsavedChanges(false);
            conflictHandledRef.current = false;
            await refetchWorksheet();
          } catch (error) {
            showWorksheetConflictToast(error as AxiosError);
            throw error;
          }
        },
        onCancel: noop,
      });
    },
    [
      duplicateWorksheet,
      id,
      modalService,
      navigate,
      refetchWorksheet,
      removeFromStorage,
      saveWorksheet,
      showWorksheetConflictToast,
      worksheetDescription,
      worksheetTitle,
    ]
  );

  const saveToBE = useCallback(
    async (
      jsonBlocks: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[],
      onSaved?: () => void
    ): Promise<void> => {
      const handleWsDuplicateClick = async (): Promise<void> => {
        if (!id) return;
        const copiedWorksheet = await duplicateWorksheet({ id, modifiedContent: jsonBlocks });

        navigate(`${ProtectedRoutes.WORKSHEET_DASHBOARD}/${copiedWorksheet.id}`);
        await removeFromStorage();

        setHasUnsavedChanges(false);
        if (onSaved) {
          onSaved();
        }
      };
      const worksheetId = id || '';

      if (worksheetData?.isDistributed || isTemplateLinked) {
        let mode: ModalSaveMode = 'TEMPLATE_ONLY';
        if (worksheetData?.isDistributed && isTemplateLinked) {
          mode = 'DISTRIBUTED_TEMPLATE';
        } else if (worksheetData?.isDistributed) {
          mode = 'DISTRIBUTED';
        }

        let intent: WorksheetSaveIntent = WorksheetSaveIntent.MANUAL_SAVE_PROPAGATE_TEMPLATE;
        if (mode === 'DISTRIBUTED_TEMPLATE') {
          intent = WorksheetSaveIntent.MANUAL_SAVE_DELETE_ANSWERS_PROPAGATE_TEMPLATE;
        } else if (mode === 'DISTRIBUTED') {
          intent = WorksheetSaveIntent.MANUAL_SAVE_DELETE_ANSWERS;
        }

        modalService.show(ModalVariant.WORKSHEET_DISTRIBUTION_SAVE, {
          mode,
          onCopyAndSave: handleWsDuplicateClick,
          onPrimarySave: async () => {
            try {
              await saveWorksheet({
                id: worksheetId,
                title: worksheetTitle,
                description: worksheetDescription,
                content: jsonBlocks,
                intent,
              });
              await refetchWorksheet();
              await removeFromStorage();
              setHasUnsavedChanges(false);
              if (onSaved) {
                onSaved();
              }
            } catch (error) {
              showWorksheetConflictToast(error as AxiosError);
              throw error;
            }
          },
          onCancel: noop,
        });
        return;
      }

      await saveWorksheet({
        id: worksheetId,
        title: worksheetTitle,
        description: worksheetDescription,
        content: jsonBlocks,
        intent: WorksheetSaveIntent.MANUAL_SAVE,
      });
      await removeFromStorage();
      setHasUnsavedChanges(false);
      if (onSaved) {
        onSaved();
      }
    },
    [
      duplicateWorksheet,
      id,
      modalService,
      navigate,
      removeFromStorage,
      saveWorksheet,
      worksheetData?.isDistributed,
      isTemplateLinked,
      worksheetDescription,
      worksheetTitle,
      refetchWorksheet,
      showWorksheetConflictToast,
    ]
  );

  const handleEditorChange = (newDocument: typeof editor.document): void => {
    setHasUnsavedChanges(true);
    saveToStorage(newDocument);
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newTitle = event.target.value;
    setWorksheetTitle(newTitle);
    setHasUnsavedChanges(true);
  };

  const toggleTitleEdit = (): void => {
    setIsTitleEditable(!isTitleEditable);
  };

  useLayoutEffect(() => {
    if (isTitleEditable && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
    if (!isTitleEditable && titleInputRef.current) {
      titleInputRef.current.blur();
    }
  }, [isTitleEditable]);

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newDescription = event.target.value;
    setWorksheetDescription(newDescription);
    setHasUnsavedChanges(true);
  };

  const toggleDescriptionEdit = (): void => {
    setIsDescriptionEditable(!isDescriptionEditable);
  };

  const createDescription = (): void => {
    setWorksheetDescription('');
    setIsDescriptionEditable(true);
  };

  useLayoutEffect(() => {
    if (isDescriptionEditable && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
    if (!isDescriptionEditable && descriptionInputRef.current) {
      descriptionInputRef.current.blur();
    }
  }, [isDescriptionEditable]);

  const autoSaveData = {
    document: editor.document,
    title: worksheetTitle,
    description: worksheetDescription,
  };

  // Auto-save hook for editor.document, title, and description
  useAutoSave<typeof autoSaveData>({
    data: autoSaveData,
    saveFn: async (data) => {
      try {
        await saveWorksheet({
          id: id || '',
          title: data.title,
          description: data.description,
          content: data.document as Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[],
          intent: WorksheetSaveIntent.AUTO_SAVE,
        });
      } catch (error) {
        const axiosError = error as AxiosError;

        if (
          axiosError.response?.status === HttpStatusCode.Conflict &&
          !conflictHandledRef.current
        ) {
          conflictHandledRef.current = true;

          const result = await refetchWorksheet();

          await handleDistributedConflict(
            result.data,
            data.document as Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]
          );

          return;
        }
        showWorksheetConflictToast(axiosError);
        throw error;
      }

      await removeFromStorage();
    },
    onStatusChange: (status) => {
      setSaveStatus(status);
      if (status === SaveStatus.SAVED) {
        setHasUnsavedChanges(false);
        conflictHandledRef.current = false; // reset after successful save
      }
    },
    delay: 5000,
    disabled: worksheetData?.isDistributed || isTemplateLinked || !isOwner,
  });

  const setMode = useCallback(
    (nextMode: WorksheetMode): void => {
      if (!id) return;
      const nextParams = new URLSearchParams(location.search);
      nextParams.set('mode', nextMode);

      setSearchParams(nextParams);
    },
    [id, location.search, setSearchParams]
  );

  const handleDistribute = useCallback((): void => {
    if (!id) return;
    modalService.show(ModalVariant.WORKSHEET_DISTRIBUTION, {
      worksheetId: id,
      worksheetTitle,
    });
  }, [id, modalService, worksheetTitle]);

  const handleSaveClick = useCallback(async (): Promise<void> => {
    try {
      await saveToBE(editor.document);
      refetchWorksheet();
    } catch (error) {
      showWorksheetConflictToast(error as AxiosError);
      throw error;
    }
  }, [editor.document, refetchWorksheet, saveToBE, showWorksheetConflictToast]);

  const handlePrint = useCallback(async (): Promise<void> => {
    const result = await printWorksheet({
      editor,
      worksheetTitle,
    });

    if (!result.success) {
      toast.show({
        variant: ToastVariant.ERROR,
        title: 'Drucken fehlgeschlagen',
        description: result.error || 'Ein unbekannter Fehler ist aufgetreten.',
      });
    }
  }, [editor, worksheetTitle, toast]);

  useUnsavedChanges({
    hasUnsavedChanges,
    onSaveAndLeave: async (continueNavigation) => {
      await saveToBE(editor.document, continueNavigation);
    },
    message: 'Du hast ungespeicherte Änderungen. Möchtest du die Seite wirklich verlassen?',
  });

  const topButtons = useMemo((): ReactElement => {
    const buttons: ReactElement[] = [];

    if (isOwner) {
      buttons.push(
        <Button
          key="edit-preview"
          variant="secondaryOutline"
          onClick={() => setMode(isPreviewMode ? 'edit' : 'preview')}
          backgroundColor={isPreviewMode ? 'background.subtle.green' : 'transparent'}
          borderColor={isPreviewMode ? 'border.accent.active' : 'border.accent'}
          color="text.secondary"
        >
          {isPreviewMode ? (
            <NovaIcon icon={EditIcon} size="m" color="text.secondary" aria-hidden />
          ) : (
            <NovaIcon icon={VisibilityIcon} size="m" color="text.secondary" aria-hidden />
          )}
          {isPreviewMode ? 'Bearbeiten' : 'Vorschau'}
        </Button>
      );

      if (
        (modeParam === 'edit' || modeParam === 'preview') &&
        featureFlags.isEnabled('isWorksheetPrintingEnabled')
      ) {
        buttons.push(
          <Button key="print" variant="secondary" size="m" onClick={handlePrint}>
            <NovaIcon icon={PrintIcon} size="m" color="text.secondary" aria-hidden />
            Drucken
          </Button>
        );
      }

      buttons.push(
        <Button key="distribute" variant="primary" onClick={handleDistribute}>
          Verteilen
        </Button>
      );

      if (worksheetData?.isDistributed) {
        buttons.unshift(
          <Button
            variant="secondaryOutline"
            onClick={() => setMode(modeParam === 'review' ? 'edit' : 'review')}
            backgroundColor={modeParam === 'review' ? 'green.quaternary' : 'transparent'}
            borderColor={modeParam === 'review' ? 'green.primary' : 'border.accent'}
            color="text.secondary"
          >
            {modeParam === 'review' ? (
              <NovaIcon icon={EditIcon} size="m" color="text.secondary" aria-hidden />
            ) : (
              <NovaIcon icon={DiagramIcon} size="m" color="text.secondary" aria-hidden />
            )}
            {modeParam === 'review' ? 'Bearbeiten' : 'Ergebnisse'}
          </Button>
        );
      }

      const shouldShowSaveButton =
        (worksheetData?.isDistributed || isTemplateLinked) &&
        !(saveStatus === SaveStatus.IDLE && !hasUnsavedChanges);
      if (shouldShowSaveButton) {
        buttons.unshift(
          <Button
            key="save"
            onClick={handleSaveClick}
            variant="secondaryOutline"
            borderColor="border.accent"
            color="text.secondary"
          >
            <NovaIcon icon={SaveIcon} size="m" color="text.secondary" aria-hidden />
            Speichern
          </Button>
        );
      }
    } else if (worksheetData?.isDistributed) {
      // Non-owner: only Ergebnisse / Vorschau toggle when distributed
      buttons.push(
        <Button
          key="results-preview"
          variant="secondaryOutline"
          onClick={() => setMode(isReviewMode ? 'preview' : 'review')}
          backgroundColor={isReviewMode ? 'green.quaternary' : 'transparent'}
          borderColor={isReviewMode ? 'green.primary' : 'border.accent'}
          color="text.secondary"
        >
          {isReviewMode ? (
            <NovaIcon icon={VisibilityIcon} size="m" color="text.secondary" aria-hidden />
          ) : (
            <NovaIcon icon={DiagramIcon} size="m" color="text.secondary" aria-hidden />
          )}
          {isReviewMode ? 'Vorschau' : 'Ergebnisse'}
        </Button>
      );
    }

    return (
      <HStack gap="30px">
        {isOwner && !worksheetData?.isDistributed && !isTemplateLinked && (
          <SaveIndicator
            isSaved={
              saveStatus === SaveStatus.SAVED ||
              (saveStatus === SaveStatus.IDLE && !hasUnsavedChanges)
            }
          />
        )}
        <HStack gap="15px">
          {buttons.map((buttonComponent, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`button-${index}`}>{buttonComponent}</React.Fragment>
          ))}
        </HStack>
      </HStack>
    );
  }, [
    handleDistribute,
    handlePrint,
    handleSaveClick,
    hasUnsavedChanges,
    isOwner,
    isPreviewMode,
    isReviewMode,
    saveStatus,
    setMode,
    worksheetData?.isDistributed,
    isTemplateLinked,
    modeParam,
    featureFlags,
  ]);

  const worksheetEditorOptions = useMemo(() => {
    return {
      isViewMode: isPreviewMode,
      title: worksheetData?.title || 'Arbeitsblatt',
      ButtonComponents: topButtons,
    };
  }, [isPreviewMode, topButtons, worksheetData?.title]);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetchWorksheet()} />;
  }

  return (
    <PageContext
      hasBackButton
      title={worksheetData?.title || 'Arbeitsblatt'}
      headTitle={worksheetData?.title || 'Arbeitsblatt'}
      worksheetEditorOptions={worksheetEditorOptions}
    >
      <>
        {!isDesktop && (
          <Box
            width="100%"
            bg="background.highlight.yellow"
            borderBottom="default"
            borderColor="border.accent"
            py="m"
            px="l"
          >
            <Text textStyle="body.s.medium" color="text.primary" textAlign="center">
              Für die beste Erfahrung empfehlen wir, diese Seite auf einem Desktop-Gerät zu öffnen.
            </Text>
          </Box>
        )}
        <Box
          bg="background.primary"
          flexGrow={1}
          my="40px"
          p="4xl"
          height="100%"
          width="100%"
          borderRadius={{ md: '2xl' }}
        >
          <WorksheetHeader
            isReviewMode={isReviewMode}
            worksheetTitle={worksheetTitle}
            isTitleEditable={isOwner && isTitleEditable}
            titleInputRef={titleInputRef}
            onTitleChange={handleTitleChange}
            onTitleClick={isOwner && !isTitleEditable ? toggleTitleEdit : undefined}
            onStopTitleEdit={() => {
              setIsTitleEditable(false);
            }}
            onResetTitle={() => {
              setWorksheetTitle(worksheetData?.title || 'Arbeitsblatt');
              setIsTitleEditable(false);
            }}
            topButtons={topButtons}
          />

          {isPreviewMode && (
            <WorksheetPreview<typeof schema.blockSchema>
              originDocument={editor.document}
              schema={schema}
            />
          )}
          {isEditMode && (
            <>
              <WorksheetDescription
                isDescriptionEditable={isDescriptionEditable}
                worksheetDescription={worksheetDescription}
                descriptionInputRef={descriptionInputRef}
                onDescriptionChange={handleDescriptionChange}
                onDescriptionClick={!isDescriptionEditable ? toggleDescriptionEdit : undefined}
                onResetDescription={() => {
                  setWorksheetDescription(worksheetData?.description || '');
                  setIsDescriptionEditable(false);
                }}
                onStopDescriptionEdit={() => {
                  setIsDescriptionEditable(false);
                }}
                onCreateDescription={createDescription}
              />

              <Separator />

              <WorksheetEditor
                editor={editor}
                getSlashMenuItems={getCustomSlashMenuItems}
                onEditorChange={() => handleEditorChange(editor.document)}
                shouldShowEditor={Boolean(shouldShowEditor)}
                onShowEditor={showEditor}
              />
            </>
          )}
          {isReviewMode && <WorksheetReviewContent worksheetId={id} />}
        </Box>
      </>
    </PageContext>
  );
};
